import React, { useState, useEffect, useContext } from 'react';
import { db } from '../../firebase/firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, Timestamp, writeBatch } from "firebase/firestore";
import { AuthContext } from '../../context/AuthContext';
import { Table, Button, Form, Alert, Modal, Spinner, Card } from 'react-bootstrap';
import { pricingPlansSeedData } from '../../data/pricingPlansSeedData'; // Import the seed data
import './Admin.css';

const initialPlanData = {
    name: "",
    price: "",
    priceDetails: "per annum",
    period: "(Taxes Extra)",
    features: [
        "Validity - 1 Year | Quarterly Renewal",
        "",
        "",
        "",
        "",
        "",
        ""
    ],
    recommended: false,
    inactive: false, // New inactive flag
    order: 0, // To control display order
    riskLevel: 1 // 1 = lowest risk, 5 = highest risk
};

const AdminPricingPlans = () => {
    const { user: adminUser } = useContext(AuthContext);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [uploading, setUploading] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [formData, setFormData] = useState(initialPlanData);
    
    const plansCollectionRef = collection(db, "pricingPlans");

    const fetchPlans = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getDocs(plansCollectionRef);
            const fetchedPlans = data.docs
                .map(doc => ({ ...doc.data(), id: doc.id }))
                .sort((a, b) => a.order - b.order); // Sort by order
            setPlans(fetchedPlans);
        } catch (err) {
            console.error("Error fetching pricing plans: ", err);
            setError("Failed to load pricing plans.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (adminUser?.role === 'admin') {
            fetchPlans();
        }
    }, [adminUser]);

    const handleShowModal = (plan = null) => {
        setCurrentPlan(plan);
        if (plan) {
            // Ensure features is an array of 7 strings for the form
            const features = Array.isArray(plan.features) ? plan.features : [];
            const paddedFeatures = [...features, ...Array(7 - features.length).fill('')];
            setFormData({ ...plan, features: paddedFeatures, riskLevel: plan.riskLevel || 1 });
        } else {
            setFormData(initialPlanData);
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentPlan(null);
        setFormData(initialPlanData);
        setError(null);
        setSuccess(null);
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name.startsWith('feature-')) {
            const index = parseInt(name.split('-')[1], 10);
            const newFeatures = [...formData.features];
            newFeatures[index] = value;
            setFormData(prev => ({ ...prev, features: newFeatures }));
        } else if (name === 'price' || name === 'order' || name === 'riskLevel') {
             setFormData(prev => ({ ...prev, [name]: Number(value) }));
        }
        else {
            setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        }
    };

    const handleSavePlan = async () => {
        if (adminUser?.role !== 'admin') {
            setError("Unauthorized action.");
            return;
        }
        setError(null);
        setSuccess(null);

        // Filter out empty features
        const finalFeatures = formData.features.filter(f => f.trim() !== '');

        const planData = {
            ...formData,
            features: finalFeatures,
            updatedAt: Timestamp.now()
        };

        try {
            if (currentPlan) {
                // Update
                const planDocRef = doc(db, 'pricingPlans', currentPlan.id);
                await updateDoc(planDocRef, planData);
                setSuccess("Plan updated successfully!");
            } else {
                // Create
                planData.createdAt = Timestamp.now();
                await addDoc(plansCollectionRef, planData);
                setSuccess("Plan created successfully!");
            }
            fetchPlans();
            handleCloseModal();
        } catch (err) {
            console.error("Error saving plan: ", err);
            setError("Failed to save plan.");
        }
    };

    const handleDeletePlan = async (planId) => {
        if (adminUser?.role !== 'admin' || !window.confirm('Are you sure you want to delete this plan?')) {
            return;
        }
        setError(null);
        setSuccess(null);
        try {
            const planDocRef = doc(db, 'pricingPlans', planId);
            await deleteDoc(planDocRef);
            setSuccess("Plan deleted successfully!");
            fetchPlans();
        } catch (err) {
            console.error("Error deleting plan: ", err);
            setError("Failed to delete plan.");
        }
    };

    const handleInitialUpload = async () => {
        if (!window.confirm("Are you sure you want to upload the initial pricing plans? This is intended for one-time setup and may create duplicates if run again.")) {
            return;
        }
        setUploading(true);
        setError(null);
        setSuccess(null);
        try {
            const batch = writeBatch(db);
            const plansCollectionRef = collection(db, 'pricingPlans');
            pricingPlansSeedData.forEach((plan) => {
                const docRef = doc(plansCollectionRef); // Create a new doc with a unique ID
                batch.set(docRef, plan);
            });
            await batch.commit();
            setSuccess("Seed data uploaded successfully! Refreshing plans...");
            fetchPlans(); // Refresh the list
        } catch (err) {
            console.error("Error uploading seed data: ", err);
            setError("Failed to upload seed data.");
        } finally {
            setUploading(false);
        }
    };

    if (loading) {
        return <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}><Spinner animation="border" /></div>;
    }

    return (
        <div>
            <Card className="mb-4 admin-card bg-light-warning">
                <Card.Header as="h5">One-Time Data Setup</Card.Header>
                <Card.Body>
                    <Card.Text>
                        If this is the first time setting up, click this button to upload the initial pricing plans to the database.
                    </Card.Text>
                    <Button
                        variant="warning"
                        disabled={uploading}
                        onClick={handleInitialUpload}
                    >
                        {uploading ? <Spinner as="span" animation="border" size="sm" /> : 'Upload Initial Pricing Plans'}
                    </Button>
                </Card.Body>
            </Card>

            <h2 className="admin-page-title">Manage Pricing Plans</h2>
            {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}
            <Button variant="primary" onClick={() => handleShowModal()} className="mb-3">
                Add New Plan
            </Button>
            
            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        <th>Order</th>
                        <th>Name</th>
                        <th>Price</th>
                        <th>Recommended</th>
                        <th>Status</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {plans.map(plan => (
                        <tr key={plan.id}>
                            <td>{plan.order}</td>
                            <td>{plan.name}</td>
                            <td>₹{plan.price}</td>
                            <td>{plan.recommended ? 'Yes' : 'No'}</td>
                            <td>
                                <span className={`badge ${plan.inactive ? 'bg-secondary' : 'bg-success'}`}>
                                    {plan.inactive ? 'Inactive' : 'Active'}
                                </span>
                            </td>
                            <td>
                                <Button variant="info" size="sm" onClick={() => handleShowModal(plan)}>Edit</Button>{' '}
                                <Button variant="danger" size="sm" onClick={() => handleDeletePlan(plan.id)}>Delete</Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{currentPlan ? 'Edit' : 'Add'} Pricing Plan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Plan Name</Form.Label>
                            <Form.Control type="text" name="name" value={formData.name} onChange={handleFormChange} />
                        </Form.Group>
                         <Form.Group className="mb-3">
                            <Form.Label>Display Order</Form.Label>
                            <Form.Control type="number" name="order" value={formData.order} onChange={handleFormChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Price (e.g., 20000)</Form.Label>
                            <Form.Control type="number" name="price" value={formData.price} onChange={handleFormChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Price Details (e.g., per annum)</Form.Label>
                            <Form.Control type="text" name="priceDetails" value={formData.priceDetails} onChange={handleFormChange} />
                        </Form.Group>
                         <Form.Group className="mb-3">
                            <Form.Label>Period (e.g., (Taxes Extra))</Form.Label>
                            <Form.Control type="text" name="period" value={formData.period} onChange={handleFormChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check type="checkbox" label="Recommended Plan" name="recommended" checked={formData.recommended} onChange={handleFormChange} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check type="checkbox" label="Inactive Plan (Greyed out on homepage)" name="inactive" checked={formData.inactive} onChange={handleFormChange} />
                        </Form.Group>
                        
                        <h5>Features</h5>
                        {formData.features.map((feature, index) => (
                             <Form.Group className="mb-2" key={index}>
                                <Form.Label>Feature {index + 1}</Form.Label>
                                <Form.Control type="text" name={`feature-${index}`} value={feature} onChange={handleFormChange} />
                            </Form.Group>
                        ))}
                        <Form.Group className="mb-3">
                            <Form.Label>Risk Level</Form.Label>
                            <Form.Select
                                name="riskLevel"
                                value={formData.riskLevel}
                                onChange={handleFormChange}
                            >
                                <option value={1}>1 - Lowest Risk</option>
                                <option value={2}>2</option>
                                <option value={3}>3</option>
                                <option value={4}>4</option>
                                <option value={5}>5 - Highest Risk</option>
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                    <Button variant="primary" onClick={handleSavePlan}>Save Plan</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default AdminPricingPlans; 